import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import Tabs from '../tabs/push-sync.md';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const TabsBlock = makeShortcode("TabsBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "troubleshooting-push-identifier-sync"
    }}>{`Troubleshooting push identifier sync`}</h1>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`This troubleshooting guide requires Adobe Experience Platform Assurance. For more information about access and how to set up Assurance, see the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/assurance/home.html"
      }}>{`documentation`}</a>{`.`}</p>
    <p>{`When implementing push messaging via the Adobe Experience Platform Mobile SDK, you can validate the client-side implementation by verifying that you completed the following steps:`}</p>
    <ol>
      <li parentName="ol">{`Pass the device's push token to the SDK.`}</li>
      <li parentName="ol">{`Verify push token sync with the Experience Cloud Identity service.`}</li>
      <li parentName="ol">{`Ensure user opt-in for push in Adobe Analytics.`}</li>
      <li parentName="ol">{`Confirm user ID is correctly set (and is the same as Steps 2 and 3, above).`}</li>
    </ol>
    <h2 {...{
      "id": "pass-the-push-identifier-to-the-sdk"
    }}>{`Pass the push identifier to the SDK`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`setPushIdentifier`}</inlineCode>{` API sets the device token for push notifications in the SDK. This results in a few network calls made to the requisite Adobe servers to associate the user with the push token.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`If SDK privacy status isoptedout, the push identifier will not be set.`}</p>
    <h3 {...{
      "id": "setpushidentifier"
    }}>{`setPushIdentifier`}</h3>
    <TabsBlock orientation="horizontal" slots="heading, content" repeat="2" mdxType="TabsBlock" />
    <p>{`Android`}</p>
    <Tabs query="platform=android&task=set-push" mdxType="Tabs" />
    <p>{`iOS`}</p>
    <Tabs query="platform=ios&task=set-push" mdxType="Tabs" />
    {/*- React Native
     <Tabs query="platform=react-native&task=set-push"/> -*/}
    <h2 {...{
      "id": "verify-push-token-sync-with-the-experience-cloud-identity-service"
    }}>{`Verify push token sync with the Experience Cloud Identity service`}</h2>
    <p>{`Launch your app with the device connected to an `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/assurance/home.html"
      }}>{`Adobe Experience Platform Assurance session`}</a>{`.`}</p>
    <p>{`In the list of events, verify that you have an event with type `}<inlineCode parentName="p">{`SetPushIdentifier`}</inlineCode>{`. In the details panel on the right, verify the value of the push token for this device. The value in `}<inlineCode parentName="p">{`pushIdentifier`}</inlineCode>{` is the same value that is sent to the Adobe servers.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "44.6875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/5530d/set-push-token-to-identity.webp 320w", "/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/0c8fb/set-push-token-to-identity.webp 640w", "/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/94b1e/set-push-token-to-identity.webp 1280w", "/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/64296/set-push-token-to-identity.webp 1600w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/dd4a7/set-push-token-to-identity.png 320w", "/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/0f09e/set-push-token-to-identity.png 640w", "/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/bbbf7/set-push-token-to-identity.png 1280w", "/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/42cbc/set-push-token-to-identity.png 1600w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/af9de721e60b95da6683e1ac6d5cdfde/bbbf7/set-push-token-to-identity.png",
            "alt": "Verify push identifier received by the SDK",
            "title": "Verify push identifier received by the SDK",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "ensure-user-opt-in-for-push-in-adobe-analytics"
    }}>{`Ensure user opt-in for push in Adobe Analytics`}</h2>
    <p>{`Launch your app with the device connected to an `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/assurance/home.html"
      }}>{`Adobe Experience Platform Assurance session`}</a>{`.`}</p>
    <p>{`In the resulting list of events, verify that you have an event with type `}<inlineCode parentName="p">{`AnalyticsForIdentityRequest`}</inlineCode>{`. In the details panel on the right, you can see that there is a value that was sent to Analytics that opts this user in to receive push notifications.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "44.375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/00b7d3312b1014a135295eca69b8d344/5530d/push-analytics-optin.webp 320w", "/client-sdks/static/00b7d3312b1014a135295eca69b8d344/0c8fb/push-analytics-optin.webp 640w", "/client-sdks/static/00b7d3312b1014a135295eca69b8d344/94b1e/push-analytics-optin.webp 1280w", "/client-sdks/static/00b7d3312b1014a135295eca69b8d344/64296/push-analytics-optin.webp 1600w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/00b7d3312b1014a135295eca69b8d344/dd4a7/push-analytics-optin.png 320w", "/client-sdks/static/00b7d3312b1014a135295eca69b8d344/0f09e/push-analytics-optin.png 640w", "/client-sdks/static/00b7d3312b1014a135295eca69b8d344/bbbf7/push-analytics-optin.png 1280w", "/client-sdks/static/00b7d3312b1014a135295eca69b8d344/42cbc/push-analytics-optin.png 1600w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/00b7d3312b1014a135295eca69b8d344/bbbf7/push-analytics-optin.png",
            "alt": "Verify push preferences are opted in",
            "title": "Verify push preferences are opted in",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "confirm-that-the-user-id-is-correctly-set"
    }}>{`Confirm that the user ID is correctly set`}</h2>
    <p>{`Launch your app with the device connected to an `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/assurance/home.html"
      }}>{`Adobe Experience Platform Assurance session`}</a>{`.`}</p>
    <p>{`In the list of events, verify that you have an event with type `}<inlineCode parentName="p">{`UPDATED_IDENTITY_RESPONSE`}</inlineCode>{`. In the details panel on the right, confirm that the following values are correct:`}</p>
    <ul>
      <li parentName="ul">{`The value for `}<inlineCode parentName="li">{`pushidentifier`}</inlineCode>{` should match the value that was sent in step 2 above.`}</li>
      <li parentName="ul">{`The value for mid should match the value for mid that is sent to Analytics. If you are using a `}<a parentName="li" {...{
          "href": "../../../../../solution/adobe-analytics/api-reference.md#setidentifier"
        }}>{`custom visitor identifier`}</a>{`, this payload should also contain a vid variable with a value that matches the value that was used to identify this user.`}</li>
    </ul>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "42.1875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/a67a44495c210161b89a67026811f2ed/5530d/push-identities.webp 320w", "/client-sdks/static/a67a44495c210161b89a67026811f2ed/0c8fb/push-identities.webp 640w", "/client-sdks/static/a67a44495c210161b89a67026811f2ed/94b1e/push-identities.webp 1280w", "/client-sdks/static/a67a44495c210161b89a67026811f2ed/64296/push-identities.webp 1600w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/a67a44495c210161b89a67026811f2ed/dd4a7/push-identities.png 320w", "/client-sdks/static/a67a44495c210161b89a67026811f2ed/0f09e/push-identities.png 640w", "/client-sdks/static/a67a44495c210161b89a67026811f2ed/bbbf7/push-identities.png 1280w", "/client-sdks/static/a67a44495c210161b89a67026811f2ed/42cbc/push-identities.png 1600w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/a67a44495c210161b89a67026811f2ed/bbbf7/push-identities.png",
            "alt": "Verify push identifier synced",
            "title": "Verify push identifier synced",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`After completing these steps, your app is correctly configured and is ready to send push messages via the SDK and Adobe.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      